import {
  AlertOctagon,
  ArrowRight,
  Bell,
  CalendarCheck,
  Coins,
  DoorOpen,
  Gift,
  History,
  KeyRound,
  Landmark,
  ListChecks,
  Lock,
  MousePointerClick,
  PauseCircle,
  QrCode,
  ScrollText,
  Share2,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Timer,
  UserPlus,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { FaqIllustrationId } from './faqCatalog';

/**
 * One picture per FAQ answer, composed from a small set of primitives rather
 * than 27 bespoke drawings — a step strip, a payout table, a cause→fix list, a
 * tile grid, a threshold gauge. Reuse is what keeps them looking like one
 * family and keeps this file readable.
 *
 * Text lives in HTML, never in `<text>`: the same label runs 8 characters in
 * Portuguese and 19 in Spanish, and HTML wraps where SVG would overflow its
 * viewBox. SVG is reserved for what is genuinely a drawing — a proportioned
 * timeline, a chain of blocks, a die. Colours come from the design tokens so
 * the figures follow the theme.
 *
 * Each figure reads its wording from a `|`-separated i18n value; the label
 * order every figure expects is written above it.
 */

type FigureProps = { labels: string[] };

const at = (labels: string[], index: number) => labels[index] ?? '';

/* ─── Primitives ──────────────────────────────────────────────────────────── */

type Step = { icon: LucideIcon; text: string; badge?: string };

function StepRow({ steps }: { steps: Step[] }) {
  return (
    <ol className="faq-fig-steps">
      {steps.map((step, index) => (
        <li key={index} className="faq-fig-step">
          <span className="faq-fig-step-mark">
            <step.icon size={18} aria-hidden={true} />
            <b>{index + 1}</b>
          </span>
          <span className="faq-fig-step-text">{step.text}</span>
          {step.badge ? <em className="faq-fig-step-badge">{step.badge}</em> : null}
        </li>
      ))}
    </ol>
  );
}

const steps = (labels: string[], icons: LucideIcon[], badge?: { on: number; text: string }) =>
  icons.map((icon, index) => ({
    icon,
    text: at(labels, index),
    badge: badge && badge.on === index ? badge.text : undefined,
  }));

type Payout = { text: string; pay: string; tone: 'even' | 'edge' | 'exact' };

function PayoutList({ rows }: { rows: Payout[] }) {
  return (
    <ul className="faq-fig-payouts">
      {rows.map((row) => (
        <li key={row.pay + row.text}>
          <span className="faq-fig-payout-text">{row.text}</span>
          <b className={`faq-fig-payout-chip faq-pay-${row.tone}`}>{row.pay}</b>
        </li>
      ))}
    </ul>
  );
}

/** Each thing that goes wrong, paired with the one thing that fixes it. */
function CauseFixList({ rows }: { rows: Array<{ cause: string; fix: string }> }) {
  return (
    <ul className="faq-fig-causes">
      {rows.map((row) => (
        <li key={row.cause}>
          <span className="faq-fig-cause">
            <AlertOctagon size={15} aria-hidden={true} />
            {row.cause}
          </span>
          <ArrowRight size={14} className="faq-fig-cause-arrow" aria-hidden={true} />
          <span className="faq-fig-fix">{row.fix}</span>
        </li>
      ))}
    </ul>
  );
}

function TileGrid({ tiles, columns }: { tiles: Array<{ icon: LucideIcon; text: string }>; columns: number }) {
  return (
    <ul className="faq-fig-tiles" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {tiles.map((tile, index) => (
        <li key={index}>
          <span className="faq-fig-tile-mark">
            <tile.icon size={17} aria-hidden={true} />
          </span>
          <span className="faq-fig-tile-text">{tile.text}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A bar with marks along it. Used wherever the answer is really "where does
 * the line sit" — the lock buffer, the entry count that makes a draw run.
 */
function Gauge({
  segments,
  ticks,
  legend,
  ariaLabel,
}: {
  segments: Array<{ width: number; color: string; tint: string }>;
  ticks: Array<{ x: number; text: string; anchor: 'start' | 'middle' | 'end' }>;
  legend: Array<{ color: string; text: string }>;
  ariaLabel: string;
}) {
  let cursor = 6;
  return (
    <>
      <svg viewBox="0 0 300 56" className="faq-fig-svg" role="img" aria-label={ariaLabel}>
        {segments.map((segment, index) => {
          const x = cursor;
          cursor += segment.width + 2;
          return (
            <rect
              key={index}
              x={x}
              y="10"
              width={segment.width}
              height="20"
              rx={segment.width < 20 ? 4 : 6}
              fill={segment.tint}
              stroke={segment.color}
              strokeWidth="1.5"
            />
          );
        })}
        {ticks.map((tick) => (
          <g key={tick.text}>
            <line x1={tick.x} y1="32" x2={tick.x} y2="38" stroke="var(--ds-ink-4)" strokeWidth="1.5" />
            <text
              x={tick.x}
              y="50"
              textAnchor={tick.anchor}
              fill="var(--ds-ink-3)"
              fontSize="11"
              fontWeight="700"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
            >
              {tick.text}
            </text>
          </g>
        ))}
      </svg>
      <ul className="faq-fig-legend">
        {legend.map((item) => (
          <li key={item.text}>
            <i style={{ background: item.color }} aria-hidden={true} />
            <span>{item.text}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

const DICE_PIPS: Record<number, Array<[number, number]>> = {
  1: [[12, 12]],
  2: [[7, 7], [17, 17]],
  3: [[7, 7], [12, 12], [17, 17]],
  4: [[7, 7], [17, 7], [7, 17], [17, 17]],
  5: [[7, 7], [17, 7], [12, 12], [7, 17], [17, 17]],
  6: [[7, 6], [17, 6], [7, 12], [17, 12], [7, 18], [17, 18]],
};

function Die({ n }: { n: number }) {
  return (
    <span className="faq-fig-die">
      <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden={true}>
        <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#fef3c7" stroke="#78350f" strokeWidth="0.6" />
        {(DICE_PIPS[n] ?? []).map(([cx, cy], index) => (
          <circle key={index} cx={cx} cy={cy} r="1.8" fill="#1c1917" />
        ))}
      </svg>
    </span>
  );
}

/* ─── Figures ─────────────────────────────────────────────────────────────── */

/** labels: 4 step names */
const StartFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [UserPlus, QrCode, DoorOpen, Coins])} />
);

/** labels: 3 step names, then the reward badge */
const AppFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [Smartphone, Share2, Sparkles], { on: 2, text: at(labels, 3) })} />
);

/** labels: 3 step names, then the speed badge */
const DepositFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [QrCode, Landmark, Wallet], { on: 1, text: at(labels, 3) })} />
);

/** labels: 3 step names, then the speed badge */
const WithdrawFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [MousePointerClick, ShieldCheck, Wallet], { on: 2, text: at(labels, 3) })} />
);

/** labels: what is accepted, what is not */
const CurrenciesFigure = ({ labels }: FigureProps) => (
  <ul className="faq-fig-yesno">
    <li className="is-yes">
      <b aria-hidden={true}>✓</b>
      <span>{at(labels, 0)}</span>
    </li>
    <li className="is-no">
      <b aria-hidden={true}>✕</b>
      <span>{at(labels, 1)}</span>
    </li>
  </ul>
);

/**
 * labels: 3 timeline steps, then cause 1-2 and fix 1-2.
 *
 * The timeline comes first on purpose: most people asking this are inside the
 * ~36 second gap and just need to see that a gap is supposed to exist. Only
 * then does the cause→fix pair matter.
 */
const PrizeMissingFigure = ({ labels }: FigureProps) => (
  <>
    <StepRow steps={steps(labels, [Lock, Timer, Wallet])} />
    <ul className="faq-fig-causes faq-fig-causes-spaced">
      {[
        { cause: at(labels, 3), fix: at(labels, 5) },
        { cause: at(labels, 4), fix: at(labels, 6) },
      ].map((row) => (
        <li key={row.cause}>
          <span className="faq-fig-cause">
            <AlertOctagon size={15} aria-hidden={true} />
            {row.cause}
          </span>
          <ArrowRight size={14} className="faq-fig-cause-arrow" aria-hidden={true} />
          <span className="faq-fig-fix">{row.fix}</span>
        </li>
      ))}
    </ul>
  </>
);

/** labels: the caption under the chain */
function RiggedFigure({ labels }: FigureProps) {
  return (
    <>
      <svg viewBox="0 0 300 96" className="faq-fig-svg" role="img" aria-label={at(labels, 0)}>
        {/* a reach for the blocks, blocked */}
        <line x1="150" y1="10" x2="150" y2="30" stroke="var(--ds-lose)" strokeWidth="2" strokeDasharray="4 3" />
        <circle cx="150" cy="16" r="11" fill="none" stroke="var(--ds-lose)" strokeWidth="2" />
        <line x1="142" y1="8" x2="158" y2="24" stroke="var(--ds-lose)" strokeWidth="2" />

        <line x1="46" y1="62" x2="254" y2="62" stroke="var(--ds-border-strong)" strokeWidth="2" />
        {[46, 150, 254].map((cx, index) => (
          <g key={cx}>
            <rect x={cx - 30} y={44} width={60} height={36} rx={8} fill="var(--ds-surface-3)" stroke="var(--ds-signal)" strokeWidth="1.5" />
            <text x={cx} y={67} textAnchor="middle" fill="var(--ds-signal-hi)" fontSize="15" fontWeight="800" fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace">
              {`#${index + 1}`}
            </text>
          </g>
        ))}
      </svg>
      {/* No icon — the crossed circle already carries that meaning, and this
          line is reassurance, not a warning. */}
      <p className="faq-fig-caption">{at(labels, 0)}</p>
    </>
  );
}

/**
 * labels: hash-tail label, the three block rows' label, the sum label, the
 * result label. The digits are a worked sample, deliberately only 4 of the 10
 * columns wide so it stays readable on a phone.
 */
function CalcFigure({ labels }: FigureProps) {
  const rows = [
    [7, 3, 9, 1],
    [4, 8, 2, 6],
    [5, 1, 7, 0],
  ];
  const sums = [16, 12, 18, 7];
  return (
    <div className="faq-fig-calc">
      <p className="faq-fig-calc-hash">
        <span className="faq-fig-calc-dim">0x9c4b…</span>
        <span className="faq-fig-calc-tail">7391</span>
        <span className="faq-fig-calc-dim">…</span>
      </p>
      <p className="faq-fig-calc-note">{at(labels, 0)}</p>

      <div className="faq-fig-calc-grid">
        {rows.map((row, index) => (
          <div key={index} className="faq-fig-calc-row">
            <span className="faq-fig-calc-tag">{`#${index + 1}`}</span>
            {row.map((digit, column) => (
              <span key={column} className="faq-fig-calc-cell">{digit}</span>
            ))}
            <span className="faq-fig-calc-more">…</span>
          </div>
        ))}
        <div className="faq-fig-calc-row is-sum">
          <span className="faq-fig-calc-tag">{at(labels, 1)}</span>
          {sums.map((sum, column) => (
            <span key={column} className="faq-fig-calc-cell">{sum}</span>
          ))}
          <span className="faq-fig-calc-more">…</span>
        </div>
        <div className="faq-fig-calc-row is-result">
          <span className="faq-fig-calc-tag">{at(labels, 2)}</span>
          {sums.map((sum, column) => (
            <span key={column} className="faq-fig-calc-cell">{sum % 10}</span>
          ))}
          <span className="faq-fig-calc-more">…</span>
        </div>
      </div>
      <p className="faq-fig-caption">{at(labels, 3)}</p>
    </div>
  );
}

/** labels: 3 step names */
const VerifyFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [ScrollText, Landmark, ShieldCheck])} />
);

/**
 * labels: the healthy case, the stalled case, then what happens in each.
 * Green on both fixes, because both outcomes are fine for the player — the
 * point of the answer is that neither one loses their money.
 */
const ChainStallFigure = ({ labels }: FigureProps) => (
  <ul className="faq-fig-causes is-neutral">
    {[
      { cause: at(labels, 0), fix: at(labels, 2) },
      { cause: at(labels, 1), fix: at(labels, 3) },
    ].map((row) => (
      <li key={row.cause}>
        <span className="faq-fig-cause">
          <Lock size={15} aria-hidden={true} />
          {row.cause}
        </span>
        <ArrowRight size={14} className="faq-fig-cause-arrow" aria-hidden={true} />
        <span className="faq-fig-fix">{row.fix}</span>
      </li>
    ))}
  </ul>
);

/** labels: 3 legend names. Segments are to scale — the buffer really is a sliver. */
const LockFigure = ({ labels }: FigureProps) => (
  <Gauge
    ariaLabel={at(labels, 0)}
    segments={[
      { width: 222, color: 'var(--ds-win)', tint: 'var(--ds-win-tint)' },
      { width: 16, color: 'var(--ds-money)', tint: 'var(--ds-money-tint)' },
      { width: 46, color: 'var(--ds-signal)', tint: 'var(--ds-signal-tint)' },
    ]}
    ticks={[
      { x: 6, text: '0:00', anchor: 'start' },
      { x: 230, text: '4:50', anchor: 'middle' },
      { x: 294, text: '5:00', anchor: 'end' },
    ]}
    legend={[
      { color: 'var(--ds-win)', text: at(labels, 0) },
      { color: 'var(--ds-money)', text: at(labels, 1) },
      { color: 'var(--ds-signal)', text: at(labels, 2) },
    ]}
  />
);

/** labels: 3 step names, then the press-and-hold badge */
const HowToBetFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [Coins, MousePointerClick, ShieldCheck], { on: 1, text: at(labels, 3) })} />
);

/**
 * labels: entry rooms, VIP rooms, then the caption.
 *
 * This used to be a bar with "min" and "max" ticks and no numbers on it, which
 * told the reader nothing they had not already read in the question. The real
 * answer is that the cap is a property of the room and some rooms are gated —
 * two tiles say that, an empty axis does not.
 */
const BetLimitsFigure = ({ labels }: FigureProps) => (
  <>
    <TileGrid columns={2} tiles={[
      { icon: Coins, text: at(labels, 0) },
      { icon: Lock, text: at(labels, 1) },
    ]} />
    <p className="faq-fig-caption">{at(labels, 2)}</p>
  </>
);

/** labels: cause 1-3, then fix 1-3 */
const RejectedFigure = ({ labels }: FigureProps) => (
  <CauseFixList
    rows={[
      { cause: at(labels, 0), fix: at(labels, 3) },
      { cause: at(labels, 1), fix: at(labels, 4) },
      { cause: at(labels, 2), fix: at(labels, 5) },
    ]}
  />
);

/** labels: the two places to look */
const HistoryFigure = ({ labels }: FigureProps) => (
  <TileGrid columns={2} tiles={[
    { icon: History, text: at(labels, 0) },
    { icon: ScrollText, text: at(labels, 1) },
  ]} />
);

/** labels: 3 payout descriptions, then the label for the deciding digit */
const LastDigitFigure = ({ labels }: FigureProps) => (
  <div className="faq-fig-split">
    <div className="faq-fig-digit">
      <span className="faq-fig-digit-label">{at(labels, 3)}</span>
      <span className="faq-fig-digit-box">7</span>
    </div>
    <PayoutList
      rows={[
        { text: at(labels, 0), pay: '×2', tone: 'even' },
        { text: at(labels, 1), pay: '×1,5', tone: 'edge' },
        { text: at(labels, 2), pay: '×9,5', tone: 'exact' },
      ]}
    />
  </div>
);

/** labels: 3 payout descriptions, then the caption for the dice */
const SicboFigure = ({ labels }: FigureProps) => (
  <>
    <div className="faq-fig-dice">
      <Die n={4} />
      <span aria-hidden={true}>+</span>
      <Die n={2} />
      <span aria-hidden={true}>+</span>
      <Die n={6} />
      <span aria-hidden={true}>=</span>
      <strong>12</strong>
    </div>
    <p className="faq-fig-caption">{at(labels, 3)}</p>
    {/* Tone tracks the payout, not the row: gold is the big one. The red
        `edge` tone is reserved for a bet that pays LESS than it looks like it
        should, which Sic Bo does not have. */}
    <PayoutList
      rows={[
        { text: at(labels, 0), pay: '×1,95', tone: 'even' },
        { text: at(labels, 1), pay: '×2', tone: 'even' },
        { text: at(labels, 2), pay: '×180', tone: 'exact' },
      ]}
    />
  </>
);

/** labels: 3 payout descriptions, then the caption for the pocket */
const RouletteFigure = ({ labels }: FigureProps) => (
  <>
    <div className="faq-fig-pocket-row">
      <span className="faq-fig-pocket is-red">23</span>
      <span className="faq-fig-pocket-chip">{at(labels, 3)}</span>
    </div>
    <PayoutList
      rows={[
        { text: at(labels, 0), pay: '×1,95', tone: 'even' },
        { text: at(labels, 1), pay: '×1,95', tone: 'even' },
        { text: at(labels, 2), pay: '×35', tone: 'exact' },
      ]}
    />
  </>
);

/** labels: the three prize tiers, in rank order */
const RaffleHowFigure = ({ labels }: FigureProps) => (
  <ul className="faq-fig-prizes">
    {[
      { rank: '1º', count: '1×', amount: 'R$ 500', tone: 'gold' },
      { rank: '2º', count: '5×', amount: 'R$ 50', tone: 'silver' },
      { rank: '3º', count: '10×', amount: 'R$ 20', tone: 'bronze' },
    ].map((tier, index) => (
      <li key={tier.rank} className={`is-${tier.tone}`}>
        <b>{tier.rank}</b>
        <span className="faq-fig-prize-text">{at(labels, index)}</span>
        <em>
          {tier.count} {tier.amount}
        </em>
      </li>
    ))}
  </ul>
);

/** labels: the five ways to earn a ticket */
const RaffleTicketsFigure = ({ labels }: FigureProps) => (
  <TileGrid columns={3} tiles={[
    { icon: Wallet, text: at(labels, 0) },
    { icon: Share2, text: at(labels, 1) },
    { icon: Coins, text: at(labels, 2) },
    { icon: CalendarCheck, text: at(labels, 3) },
    { icon: Smartphone, text: at(labels, 4) },
  ]} />
);

/** labels: 3 step names */
const RafflePrizeFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [Gift, Wallet, Bell])} />
);

/** labels: below the floor, running, full — then the caption */
const RaffleCancelFigure = ({ labels }: FigureProps) => (
  <>
    <Gauge
      ariaLabel={at(labels, 3)}
      segments={[
        { width: 140, color: 'var(--ds-lose)', tint: 'var(--ds-lose-tint)' },
        { width: 140, color: 'var(--ds-win)', tint: 'var(--ds-win-tint)' },
      ]}
      ticks={[
        { x: 6, text: '0', anchor: 'start' },
        { x: 148, text: '50', anchor: 'middle' },
        { x: 294, text: '100', anchor: 'end' },
      ]}
      legend={[
        { color: 'var(--ds-lose)', text: at(labels, 0) },
        { color: 'var(--ds-win)', text: at(labels, 1) },
      ]}
    />
    <p className="faq-fig-caption">{at(labels, 2)}</p>
  </>
);

/** labels: what each rung unlocks, lowest first */
const VipFigure = ({ labels }: FigureProps) => (
  <ol className="faq-fig-ladder">
    {[0, 1, 2].map((index) => (
      <li key={index} style={{ marginLeft: `${index * 14}px` }}>
        <b>{`VIP ${index + 1}`}</b>
        <span>{at(labels, index)}</span>
      </li>
    ))}
  </ol>
);

/** labels: 3 step names */
const MissionsFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [ListChecks, Gift, Wallet])} />
);

/** labels: 3 step names */
const PasswordFigure = ({ labels }: FigureProps) => (
  <StepRow steps={steps(labels, [KeyRound, Bell, ShieldCheck])} />
);

/** labels: the three controls */
const LimitsFigure = ({ labels }: FigureProps) => (
  <TileGrid columns={3} tiles={[
    { icon: Coins, text: at(labels, 0) },
    { icon: Bell, text: at(labels, 1) },
    { icon: PauseCircle, text: at(labels, 2) },
  ]} />
);

/** labels: the three assurances */
const LegalFigure = ({ labels }: FigureProps) => (
  <ul className="faq-fig-badges">
    <li>
      <b>18+</b>
      <span>{at(labels, 0)}</span>
    </li>
    <li>
      <Landmark size={22} aria-hidden={true} />
      <span>{at(labels, 1)}</span>
    </li>
    <li>
      <ShieldCheck size={22} aria-hidden={true} />
      <span>{at(labels, 2)}</span>
    </li>
  </ul>
);

const FIGURES: Record<FaqIllustrationId, (props: FigureProps) => JSX.Element> = {
  start: StartFigure,
  app: AppFigure,
  deposit: DepositFigure,
  withdraw: WithdrawFigure,
  currencies: CurrenciesFigure,
  prizemissing: PrizeMissingFigure,
  rigged: RiggedFigure,
  calc: CalcFigure,
  verify: VerifyFigure,
  chainstall: ChainStallFigure,
  lock: LockFigure,
  howtobet: HowToBetFigure,
  betlimits: BetLimitsFigure,
  rejected: RejectedFigure,
  history: HistoryFigure,
  lastdigit: LastDigitFigure,
  sicbo: SicboFigure,
  roulette: RouletteFigure,
  rafflehow: RaffleHowFigure,
  raffletickets: RaffleTicketsFigure,
  raffleprize: RafflePrizeFigure,
  rafflecancel: RaffleCancelFigure,
  vip: VipFigure,
  missions: MissionsFigure,
  password: PasswordFigure,
  limits: LimitsFigure,
  legal: LegalFigure,
};

export function FaqIllustration({ id, labels }: { id: FaqIllustrationId; labels: string[] }) {
  const Figure = FIGURES[id];
  return (
    <figure className={`faq-fig faq-fig-${id}`}>
      <Figure labels={labels} />
    </figure>
  );
}
